import { action, computed, makeObservable, observable } from 'mobx';

import { urlsShareBaseOrigin, urlToDomain } from '../../lib/utils/domains';
import { ISODateTimeString, UUID } from '../../State/EvidenceDB/DatabaseTypes';
import { ChronologicalReportingEvent, isDbNetworkEventWithSearchResults, ToSearchWorkerProcessNewEventsMessage, type FromSearchWorkerNewResultsMessage, type ToSearchWorkerStartSearchingTermsMessage } from "../../workers/NetworkEventDbSearchWebWorkerStructures";
import { mapGetOrCreate } from '../../lib/utils/mapGetOrCreate';
import { NewEventsAddedToDatabase } from '../../State/StateEvents';
import { executeRateLimited } from '../../lib/utils/executeEventually';
import type { EventSearchResults, OriginAndDestinationUrl } from '../../lib/searchNetworkEvent';
import { OriginAndDestinationUrlsObserver } from '../../lib/Analysis/OriginAndDestinationUrlsObserver';
import { RecordingDataSummary } from "../../lib/Analysis/NetworkEventUrl";
import { SearchResultsObserver } from './SearchResultsObserver';
import { DbRecordingEntryWriter } from '../../State/EvidenceDB/DbRecordingEntryWriter';
import { NonSearchChronologicalEventsObserver } from "../../lib/Analysis/ReportableEventsObserver";
import { CodeBehaviorsObserver } from '../../lib/Analysis/CodeBehaviorsObserver';
import { CookiesObserver } from '../../lib/Analysis/CookiesObserver';
import { EventsTableAnalysisResults } from './EventsTableAnalysisResults';
import { CausationGraphObserver } from '../../lib/Analysis/CausationGraphObserver';
import { GlobalContext } from './GlobalContext';
import { ProcessInfo } from '../../State/ProcessInfo';
import { CanonicalSearchTermsString } from '../../workers/PoolWorkerTypes';



export const splitContentSearchResultsByDestination = <T extends OriginAndDestinationUrl>(resultsWithMixedDestinations: Iterable<T>) => {
	const sameRegisteredDomain: {[domain: string]: T[]} = {};
	const thirdPartyDomain: {[domain: string]: T[]} = {};
	for (const result of resultsWithMixedDestinations) {
		const {originUrl, destinationUrl} = result;
		if (urlsShareBaseOrigin(destinationUrl, originUrl)) {
			(sameRegisteredDomain[urlToDomain(destinationUrl) ?? ""] ??= []).push(result);
		} else {
			(thirdPartyDomain[urlToDomain(destinationUrl) ?? ""] ??= []).push(result);
		}
	}
	return { sameRegisteredDomain: Object.entries(sameRegisteredDomain), thirdPartyDomain: Object.entries(thirdPartyDomain) };
}
export type SearchResultsDestinationTypeThenDomain<T extends OriginAndDestinationUrl> = ReturnType<typeof splitContentSearchResultsByDestination<T>>;

export const splitResultsBySearchTermAndDestination = (mixedResults: ChronologicalReportingEvent[]) => {
	const resultsBySearchTerm /* : [string, ReturnType<typeof splitContentSearchResultsByDestination>][] */ = mixedResults
		.reduce( (bySearchTerm, searchEvent) => {
			if (isDbNetworkEventWithSearchResults(searchEvent)) {
				const { searchResults } = searchEvent;
				const uniqueSearchTermsFound = [...new Set<string>(searchResults.map( r => r.searchTermAsSpecified ))];
				for (const searchTerm of uniqueSearchTermsFound) {
					const filteredSearchResults = searchResults.filter( r => r.searchTermAsSpecified === searchTerm );
					(bySearchTerm[searchTerm] ??= []).push({...searchEvent, searchResults: filteredSearchResults});
				}
			}
			return bySearchTerm;
		}, {} as {[searchTerm: string]: EventSearchResults[]})
	const resultsBySearchTermAndDestination: [string, ReturnType<typeof splitContentSearchResultsByDestination<EventSearchResults>>][] =
		Object.entries(resultsBySearchTerm)
			.map( ([searchTerm, results]) =>
				([searchTerm, splitContentSearchResultsByDestination(results)] as [string, ReturnType<typeof splitContentSearchResultsByDestination<EventSearchResults>>])
			);
	return resultsBySearchTermAndDestination;
}


// const searchTermsToKey = (searchTerms: string[]) => searchTerms.sort().join("\0");

export class EventsTableWorkerResultsState implements EventsTableAnalysisResults {
	#eventTableWorker: Worker | undefined;
	
	private _searchTerms = observable.array<string>();

	readonly cookiesObserver = new CookiesObserver();
	readonly originAndDestinationUrlsObserver: OriginAndDestinationUrlsObserver;
	readonly searchResultsObserver: SearchResultsObserver;
	readonly nonSearchChronologicalEventsObserver = new NonSearchChronologicalEventsObserver();
	readonly codeBehaviorsObserver = new CodeBehaviorsObserver();
	readonly causationGraphObserver = new CausationGraphObserver();

	private _eventsProcessed: number = 0;
	private _ofTotalEvents: number = 0;
	private _charactersProcessed: number = 0;
	private _connectionsProcessed: number = 0;
//	private _connections: number = 0;

	@computed get searchTerms() {
		return this._searchTerms;
	}
	@computed get eventsProcessed() {
		return this._eventsProcessed;
	}
	@computed get ofTotalEvents() {
		return this._ofTotalEvents;
	}
	@computed get charactersProcessed() {
		return this._charactersProcessed;
	}
	@computed get connectionsProcessed() {
		return this._connectionsProcessed;
	}


	constructor(
		private recordingUuid: UUID,
		private globalContext: GlobalContext,
	) {
		makeObservable<this, "_eventsProcessed" | "_ofTotalEvents" | "_charactersProcessed" | "_connectionsProcessed">(this, {
			_eventsProcessed: observable,
			_ofTotalEvents: observable,
			_charactersProcessed: observable,
			_connectionsProcessed: observable,
		});
		// this.#updateConnectionCount();
		const {licenseCache} = this.globalContext;
		this.originAndDestinationUrlsObserver = new OriginAndDestinationUrlsObserver(licenseCache);
		this.searchResultsObserver = new SearchResultsObserver(licenseCache);
		ProcessInfo.log("Creating EventsTableWorkerResultsState for recording", recordingUuid, this.globalContext.NetworkEventDbSearchWebWorkerUrl);
		if (this.globalContext.extensionSettings.settings.searchInRealTime ||
			window.location.href.includes("recording")) {
				this.#eventTableWorker = new Worker(new URL(this.globalContext.NetworkEventDbSearchWebWorkerUrl, import.meta.url), {type: 'module'});
				this.#eventTableWorker.addEventListener("message", this.onMessageFromWorker);
		}
	}

	dataSummaryNeedsUpdate = false;


	updateRecordingStatisticsInDatabase = executeRateLimited(0, 2000, {finalExecutionOnPageHide: true})(async () => {
		this.dataSummaryNeedsUpdate = false;
		const sourcePageRegisteredDomains = [...this.originAndDestinationUrlsObserver.sourcePageOriginStatisticsTree.values()].map(
			({registeredDomain, whenFirstObservedIso, whenLastObservedIso, numberOfRequests}) => (
				{registeredDomain, whenFirstObservedIso, whenLastObservedIso, numberOfRequests})
		);
		const destinationRegisteredDomains = [...this.originAndDestinationUrlsObserver.destinationOriginStatisticsTree.values()].map(
			({registeredDomain, whenFirstObservedIso, whenLastObservedIso, numberOfRequests}) => (
				{registeredDomain, whenFirstObservedIso, whenLastObservedIso, numberOfRequests})
		);
		const dataSummary: RecordingDataSummary = {
			numberOfEvents: this.eventsProcessed,
			numberOfRequests: this.connectionsProcessed,
			whenFirstObservedIso: this.originAndDestinationUrlsObserver.whenFirstObservedIso ?? ISODateTimeString(),
			whenLastObservedIso: this.originAndDestinationUrlsObserver.whenLastObservedIso ?? ISODateTimeString(),
			sourcePageRegisteredDomains,
			destinationRegisteredDomains,
		};
		await DbRecordingEntryWriter.updateRecording(this.recordingUuid, {dataSummary});
	});
	

	protected onMessageFromWorker = action ( (e: MessageEvent<unknown>) => {
		const message = e.data as FromSearchWorkerNewResultsMessage;
		if (message.type === 'search-results') {
			this._eventsProcessed = message.eventsProcessed;
			this._ofTotalEvents = message.ofTotalEvents;
			this._connectionsProcessed = message.connectionsProcessed;
			this._charactersProcessed = message.charactersProcessed;
			this.causationGraphObserver.integrateUpdates(message.causationNodeUpdates);
			this.searchResultsObserver.update(message.searchResultsUpdates);
			this.nonSearchChronologicalEventsObserver.add(message.chronologicalEventsUpdate);
			this.originAndDestinationUrlsObserver.integrateChanges(message.sourceDestUrlsUpdates);
			this.cookiesObserver.integrateChanges(message.cookieUpdates);
			this.codeBehaviorsObserver.integrateUpdates(message.codeBehaviorUpdates);
		}
		if (message.allEventsProcessedAndSearched) {
			this.updateRecordingStatisticsInDatabase();
		}
	});

	setSearchTerms = action( (searchTerms: string[]) => {
		const canonicalSearchTermsString = CanonicalSearchTermsString(searchTerms);
		const holdResultsUntilFinished = this.searchResultsObserver.hasSearchResultsWithNetworkRequestInformation.length > 0;
		this.#eventTableWorker?.postMessage({
			type: 'start-searching-terms',
			recordingUuid: this.recordingUuid,
			poolWorkerUrl: this.globalContext.PoolWorkerUrl,
			canonicalSearchTermsString: canonicalSearchTermsString,
			searchOptions: {
				searchDataReceived: true,
			},
			includeUserEvents: true,
			holdSearchResultsUntilFinished: holdResultsUntilFinished,
		} satisfies ToSearchWorkerStartSearchingTermsMessage);
	})


	/**
	 * Ask the search worker to update its results for new events recorded 
	 **/
	update = executeRateLimited(0, 1000)( async () => {
		// Update the connection count to reflect the new events recorded.
		// ProcessInfo.log("Asking worker to process new events");
		this.#eventTableWorker?.postMessage({
			type: 'process-new-events',
			poolWorkerUrl: this.globalContext.PoolWorkerUrl,
			recordingUuid: this.recordingUuid,
		} satisfies ToSearchWorkerProcessNewEventsMessage);
	});

	static #forRecordingUuid = new Map<UUID, EventsTableWorkerResultsState>();

	static forRecordingUuid = (recordingUuid: UUID) => 
		EventsTableWorkerResultsState.#forRecordingUuid.get(recordingUuid);

	static forRecordingUuidAndSearchTerms = (recordingUuid: UUID, searchTerms: string[], globalContext: GlobalContext) => {
		const eventSearchState = mapGetOrCreate(EventsTableWorkerResultsState.#forRecordingUuid, recordingUuid, () =>
			new EventsTableWorkerResultsState(recordingUuid, globalContext));
		eventSearchState.setSearchTerms(searchTerms);
		return eventSearchState;
	}
}

NewEventsAddedToDatabase.addListener( ({recordingUuid}) => {
	const eventTableWorkerResultsState = EventsTableWorkerResultsState.forRecordingUuid(recordingUuid);
	if (eventTableWorkerResultsState != null) {
		eventTableWorkerResultsState.update();
		eventTableWorkerResultsState.dataSummaryNeedsUpdate = true;
	}
});